import { HomeSection, HomeSectionType, SliderItem } from "../../domain";
import { HomeContent } from "../../domain/repositories/home-repository";
import {
  Endpoints,
  HomeRemoteDataSourceError,
  RemoteDataSource,
} from "../core";

export class HomeRemoteDataSource extends RemoteDataSource {
  public async getHomeContent(): Promise<HomeContent> {
    try {
      const res = await this.get(Endpoints.home);
      return {
        slider: res.data.slider.map((json: any) =>
          new SliderItem().deserialize(json)
        ),
        sections: res.data.sections.map((json: any) =>
          new HomeSection().deserialize(json)
        ),
      };
    } catch (error) {
      if (!error.response) throw HomeRemoteDataSourceError.networkError();
      throw HomeRemoteDataSourceError.serverError();
    }
  }

  public async addSliderItem(link: string, image: File): Promise<void> {
    try {
      const body = new FormData();
      body.append("link", link);
      body.append("image", image);
      await this.post(Endpoints.slider, {
        body: body,
        headers: { "Content-Type": "multipart/form-data" },
      });
    } catch (error) {
      if (!error.response) throw HomeRemoteDataSourceError.networkError();
      throw HomeRemoteDataSourceError.serverError();
    }
  }

  public async deleteSliderItem(id: string): Promise<void> {
    try {
      await this.delete(Endpoints.slider + `${id}/`);
    } catch (error) {
      if (!error.response) throw HomeRemoteDataSourceError.networkError();
      throw HomeRemoteDataSourceError.serverError();
    }
  }

  public async editSliderItem(
    id: string,
    info: { link?: string | undefined; image?: File | undefined }
  ): Promise<void> {
    try {
      const { image, link } = info;
      const body = new FormData();
      if (image !== undefined) body.append("image", image);
      if (link !== undefined) body.append("link", link);
      await this.patch(Endpoints.slider + `${id}/`, {
        body,
        headers: { "Content-Type": "multipart/form-data" },
      });
    } catch (error) {
      if (!error.response) throw HomeRemoteDataSourceError.networkError();
      throw HomeRemoteDataSourceError.serverError();
    }
  }

  public async addHomeSection(
    type: HomeSectionType,
    images: { link: string; image: File }[],
    products: string[],
    title?: string,
    description?: string
  ): Promise<void> {
    try {
      const body = new FormData();
      if (title !== undefined) body.append("title", title);
      if (description !== undefined) body.append("description", description);
      body.append("type", type);
      for (let index = 0; index < products.length; index++) {
        const element = products[index];
        body.append("products", element);
      }
      for (let index = 0; index < images.length; index++) {
        const element = images[index];
        body.append(element.image.name, element.image);
        body.append(
          "images",
          JSON.stringify({
            image: element.image.name,
            link: element.link,
          })
        );
      }
      await this.post(Endpoints.homeSection, {
        body: body,
        headers: { "Content-Type": "multipart/form-data" },
      });
    } catch (error) {
      if (!error.response) throw HomeRemoteDataSourceError.networkError();
      throw HomeRemoteDataSourceError.serverError();
    }
  }

  public async deleteHomeSection(id: string): Promise<void> {
    try {
      await this.delete(Endpoints.homeSection + `${id}/`);
    } catch (error) {
      if (!error.response) throw HomeRemoteDataSourceError.networkError();
      throw HomeRemoteDataSourceError.serverError();
    }
  }

  public async editHomeSectionInfo(
    id: string,
    info: {
      title?: string;
      description?: string;
      active?: boolean;
      products?: string[];
    }
  ): Promise<void> {
    try {
      const { title, active, description, products } = info;
      await this.patch(Endpoints.homeSection + `${id}/`, {
        body: {
          ...(title === undefined ? {} : { title }),
          ...(description === undefined ? {} : { description }),
          ...(active === undefined ? {} : { is_active: active }),
          ...(products === undefined ? {} : { products }),
        },
      });
    } catch (error) {
      if (!error.response) throw HomeRemoteDataSourceError.networkError();
      throw HomeRemoteDataSourceError.serverError();
    }
  }

  public async editHomeSectionImage(
    id: string,
    info: { link?: string; image?: File }
  ): Promise<void> {
    try {
      const { image, link } = info;
      const body = new FormData();
      if (image !== undefined) body.append("image", image);
      if (link !== undefined) body.append("link", link);
      await this.patch("edit-link-image/" + id, {
        body,
        headers: { "Content-Type": "multipart/form-data" },
      });
    } catch (error) {
      if (!error.response) throw HomeRemoteDataSourceError.networkError();
      throw HomeRemoteDataSourceError.serverError();
    }
  }
}
